<template>
  <div class="job-manage">
    <!-- 查询条件 -->
    <vab-query-form>
      <vab-query-form-left-panel :span="3">
        <el-button icon="plus" type="primary" @click="createJob">添加岗位</el-button>
      </vab-query-form-left-panel>
      <vab-query-form-right-panel :span="21">
        <el-form inline @submit.prevent="query(1)">
          <el-form-item label="岗位名称">
            <el-input v-model="queryParams.jobName" clearable placeholder="请输入岗位名称" @change="query(1)" />
          </el-form-item>
          <el-form-item label="项目编号">
            <el-input v-model="queryParams.outJobCode" clearable placeholder="请输入项目编号" @change="query(1)" />
          </el-form-item>
          <el-form-item label="岗位类型">
            <el-cascader
              v-model="queryParams.categoryId"
              clearable
              filterable
              :options="categoryOptions"
              placeholder="请选择岗位类型"
              :props="{
                expandTrigger: 'hover',
                label: 'categoryName',
                value: 'id',
              }"
              @change="query(1)"
            />
          </el-form-item>
          <el-form-item label="岗位状态">
            <el-select v-model="queryParams.status" clearable placeholder="请选择岗位状态" @change="query(1)">
              <el-option v-for="item in jobStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button icon="search" type="primary" @click="query()">查询</el-button>
            <el-button icon="refresh" type="warning" @click="reset">重置</el-button>
          </el-form-item>
        </el-form>
      </vab-query-form-right-panel>
    </vab-query-form>

    <!-- 表格 -->
    <el-table v-loading="loading" border :data="tableData" :table-layout="'auto'">
      <el-table-column label="岗位名称" prop="name" show-overflow-tooltip />
      <el-table-column label="项目编号" prop="outJobCode" />
      <el-table-column label="岗位类型">
        <template #default="{ row }">
          {{ row.category.map((e) => e.categoryName).join(' / ') }}
        </template>
      </el-table-column>
      <el-table-column label="岗位描述" prop="description">
        <template #default="{ row }">
          <TextOverflow :text="row.description" />
        </template>
      </el-table-column>
      <el-table-column label="薪资范围" :width="160">
        <template #default="{ row }">{{ salaryLabel(row) }}</template>
      </el-table-column>
      <el-table-column label="推荐奖励">
        <template #default="{ row }">
          <TextOverflow :text="row.rewardName" />
        </template>
      </el-table-column>
      <el-table-column label="岗位状态" :width="100">
        <template #default="{ row }">
          {{ JOB_STATUS_CN[row.status] }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="操作" :width="160">
        <template #default="{ row }">
          <el-link type="primary" :underline="false" @click="jobEditorModal.open(row)">更改状态</el-link>
          <el-link type="primary" :underline="false" @click="editJob(row)">编辑</el-link>
        </template>
      </el-table-column>
      <template #empty>
        <el-empty class="vab-data-empty" description="暂无数据" />
      </template>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-model:current-page="queryParams.pageNum"
      background
      layout="prev, pager, next"
      :page-size="queryParams.pageSize"
      :total="queryParams.total"
      @current-change="query"
    />

    <!-- 更改状态 -->
    <el-dialog
      v-model="jobEditorModal.visible"
      destroy-on-close
      title="更改状态"
      :width="480"
      @close="jobEditorModal.close()"
    >
      <el-radio-group v-if="jobEditorModal.data" v-model="jobEditorModal.data.status">
        <el-radio v-for="item in jobStatusOptions" :key="item.value" :label="item.value">
          {{ item.label }}
        </el-radio>
      </el-radio-group>
      <template #footer>
        <el-button @click="jobEditorModal.close()">取消</el-button>
        <el-button type="primary" @click="confirmStatus">确定</el-button>
      </template>
    </el-dialog>

    <!-- 岗位编辑 -->
    <JobEditor
      :category-options="categoryOptions"
      :data="jobEditor.data"
      :type="jobEditor.type"
      :visible="jobEditor.visible"
      @close="handleJobEditorClose"
      @success="handleJobEditSuccess"
    />
  </div>
</template>

<script setup>
  import { ref, reactive, onMounted, getCurrentInstance } from 'vue'
  import { getCategoryTree, getJobList, addJob } from '@/api/recruitment'
  import { omit, last } from 'lodash'
  import { JOB_STATUS, JOB_STATUS_CN } from '../context'
  import JobEditor from './JobEditor.vue'
  import { salaryLabel } from '../helper'
  import TextOverflow from '@/components/ui/TextOverflow.vue'
  import { useModal } from '@/hooks/useModal'

  const { proxy } = getCurrentInstance()

  const jobStatusOptions = Object.values(JOB_STATUS)
    .map((e) => ({
      value: e,
      label: JOB_STATUS_CN[e],
    }))
    .filter((e) => e.value !== JOB_STATUS.UN_PUBLISHED)

  const defaultQueryParams = {
    categoryId: '',
    outJobCode: '',
    jobName: '',
    status: '',
    pageNum: 1,
    pageSize: 20,
    total: 0,
  }
  const queryParams = reactive({ ...defaultQueryParams })

  const categoryOptions = ref([])

  const loading = ref(false)
  const tableData = ref([])

  onMounted(() => {
    initCategoryOptions()
    query()
  })

  function initCategoryOptions() {
    getCategoryTree().then((res) => {
      if (res.code !== 200) return
      const { data } = res
      categoryOptions.value = data
    })
  }

  function query(pageNum = queryParams.pageNum) {
    const params = {
      ...omit(queryParams, 'total'),
      pageNum,
    }

    if (Array.isArray(params.categoryId) && params.categoryId.length) {
      params.categoryId = last(params.categoryId)
    }

    loading.value = true

    getJobList(params)
      .then(({ code, data }) => {
        if (code !== 200) return
        const { list, total } = data
        tableData.value = list
        queryParams.total = total
      })
      .finally(() => {
        loading.value = false
      })
  }

  function reset() {
    for (const k in defaultQueryParams) {
      queryParams[k] = defaultQueryParams[k]
    }
    query(1)
  }

  const jobEditor = reactive({
    type: 'add',
    visible: false,
    data: null,
  })

  function createJob() {
    jobEditor.type = 'add'
    jobEditor.data = null
    jobEditor.visible = true
  }

  function editJob(row) {
    jobEditor.type = 'edit'
    jobEditor.data = row
    jobEditor.visible = true
  }

  function handleJobEditorClose() {
    jobEditor.visible = false
    jobEditor.data = null
  }

  function handleJobEditSuccess() {
    handleJobEditorClose()
    query(1)
  }

  function confirmStatus() {
    const { id: jobId, status } = jobEditorModal.data
    addJob({ jobId, status }).then(({ code }) => {
      if (code !== 200) return
      jobEditorModal.close()
      proxy.$baseMessage(`操作成功`, 'success', 'vab-hey-message-success')
      query()
    })
  }

  const jobEditorModal = useModal()
</script>

<style lang="scss" scoped>
  .job-manage {
    padding: 16px;
  }
</style>
